import { useUserDirectory } from "@/hooks/use-user-directory";
import {
  AppModal,
  Form,
  FormSubmitButton,
  LoadingSpinner,
  SearchableSelectFormField,
  SelectFormField,
  SheetFooter,
  Slideover,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { UserProfilePhoto } from "@/components/ui/UserProfilePhoto/UserProfilePhoto";
import { useUser } from "@/components/UserProvider";
import { useCreateMatterAssignee } from "@/lib/data/react-query/matters/matter-assignee";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useCreateMatterTeamConnection } from "@/lib/data/react-query/matters/matter-team";
import { retrieveMatterTeammatesQueryOptions } from "@/lib/data/react-query/matters/matter-teammate";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type Matter, type MatterRole, type MatterTeam } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { formatUsersName } from "@/lib/utils/format";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface AddOrEditMatterAssigneeSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  creatingMore?: boolean;
  matterId: string;
  presentation?: "slideover" | "modal";
}
type ConditionalProps =
  | {
      editing: true;
      activeMatter: Matter;
    }
  | {
      editing: false;
      activeMatter?: never;
    };

const assigneeFormSchema = z.object({
  userId: z.string().min(1, { message: "Assignee is required" }),
  roleId: z.string().min(1, { message: "Role is required" }),
});

const assigneeDefaultFormValues = {
  userId: "",
  roleId: "",
} as const;

const teamFormSchema = z.object({
  teamId: z.string().min(1, { message: "Team is required" }),
});

const teamDefaultFormValues = {
  teamId: "",
} as const;

export const AddOrEditMatterAssigneeSlideover = ({
  open,
  setOpen,
  editing,
  matterId,
  activeMatter: activeMatter,
  presentation = "slideover",
}: AddOrEditMatterAssigneeSlideoverProps & ConditionalProps) => {
  const { hasActionPermission } = useUser();
  const canCreateTeamConnection = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTeamConnection,
    ACTION_PERMISSION_VALUES.create,
  );
  const canCreateAssignee = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterAssignee,
    ACTION_PERMISSION_VALUES.create,
  );
  const assigneeForm = useForm<z.infer<typeof assigneeFormSchema>>({
    resolver: zodResolver(assigneeFormSchema),
    values: editing
      ? { ...assigneeDefaultFormValues, ...activeMatter }
      : assigneeDefaultFormValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });
  const userDirectory = useUserDirectory({
    enabled: open,
    selectedIds: [assigneeForm.watch("userId")],
  });
  const {
    users,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = userDirectory;

  const teamForm = useForm<z.infer<typeof teamFormSchema>>({
    resolver: zodResolver(teamFormSchema),
    values: editing
      ? { ...teamDefaultFormValues, ...activeMatter }
      : teamDefaultFormValues,
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );

  const createMatterTeamConnection = useCreateMatterTeamConnection();
  const createMatterAssignee = useCreateMatterAssignee();

  const isTeamFormLoading = createMatterTeamConnection.isPending;
  const isAssigneeFormLoading = createMatterAssignee.isPending;
  const isLoading = isTeamFormLoading || isAssigneeFormLoading;

  const matterRoles = matterSettingsData?.data.roles ?? [];
  const teams = matterSettingsData?.data.teams ?? [];
  const selectedTeamId = teamForm.watch("teamId");
  const { data: teammatesData, isLoading: isTeammatesLoading } = useQuery(
    retrieveMatterTeammatesQueryOptions({ id: selectedTeamId }),
  );
  const teamRoster = teammatesData?.data ?? [];

  const [creatingMore] = useState(false);

  const onAssigneeSubmit = (data: z.infer<typeof assigneeFormSchema>) => {
    if (!canCreateAssignee) return;
    const user = users.find((user) => user.id === data.userId);
    if (editing) {
      setOpen(false);
    } else {
      if (user) {
        createMatterAssignee.mutate({
          dateTimeStart: new Date().toISOString(),
          matterId,
          roleId: data.roleId,
          userId: user.id,
        });
        setOpen(false);
      }
      if (creatingMore) {
        setOpen(false);
      }
    }
  };
  const onTeamSubmit = (data: z.infer<typeof teamFormSchema>) => {
    if (!canCreateTeamConnection) return;
    if (editing) {
      setOpen(false);
    } else {
      if (data.teamId) {
        createMatterTeamConnection.mutate({
          teamId: data.teamId,
          matterId,
        });
        setOpen(false);
      }
      if (creatingMore) {
        setOpen(false);
      }
    }
  };

  useEffect(() => {
    if (!open) {
      assigneeForm.reset();
      teamForm.reset();
    }
  }, [open, assigneeForm, teamForm]);

  const content = (
    <Tabs defaultValue="teams" className="flex min-h-0 flex-1 flex-col">
      <TabsList className="p-6">
        <div data-testid="teams-tab">
          <TabsTrigger value="teams">Team</TabsTrigger>
        </div>
        <div data-testid="assignees-tab">
          <TabsTrigger value="assignees">Assignee</TabsTrigger>
        </div>
      </TabsList>
      <TabsContent
        className="mt-0 flex min-h-0 flex-1 flex-col"
        value="teams"
        forceMount
      >
        <Form {...teamForm}>
          <form
            onSubmit={teamForm.handleSubmit(onTeamSubmit)}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              <fieldset
                className={cn(slideoverFieldsetClassName, "mt-0")}
                disabled={!canCreateTeamConnection}
              >
                {isTeamFormLoading && <FormLoadingOverlay />}
                <SelectFormField<MatterTeam>
                  label="Team"
                  items={teams}
                  itemsKey="id"
                  formatItemLabel={(item) => item?.label}
                  register={teamForm.register("teamId")}
                  placeholder="Select a team"
                />
                {/* Show selected team's users and roles */}
                {selectedTeamId && (
                  <div className="col-span-full mt-6">
                    <div className="bg-card w-fit rounded-lg border lg:w-130">
                      <div className="bg-muted/30 border-b px-4 py-3">
                        <Typography
                          variant="subHeading"
                          size="sm"
                          className="text-muted-foreground"
                        >
                          Team members
                        </Typography>
                      </div>
                      <div className="p-4">
                        {isTeammatesLoading ? (
                          <div className="flex items-center justify-center py-8">
                            <LoadingSpinner className="h-6 w-6" />
                            <Typography
                              size="sm"
                              className="text-muted-foreground ml-2"
                            >
                              Loading team members...
                            </Typography>
                          </div>
                        ) : teamRoster.length === 0 ? (
                          <div className="flex items-center justify-center py-8">
                            <Typography
                              size="sm"
                              className="text-muted-foreground"
                            >
                              No users on this team.
                            </Typography>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {teamRoster.map((tm) => (
                              <div
                                key={tm.id}
                                className="bg-muted/50 flex items-center justify-between gap-x-3 rounded-md border p-3 transition-colors"
                              >
                                <div className="flex items-center space-x-3">
                                  <UserProfilePhoto
                                    user={{
                                      id: tm.id,
                                      firstName: tm.firstName,
                                      lastName: tm.lastName,
                                      profilePicturePresignedUrlKey: undefined,
                                    }}
                                  />
                                  <Typography size="sm" className="font-medium">
                                    {formatUsersName(tm)}
                                  </Typography>
                                </div>
                                <div className="flex items-center">
                                  <span className="bg-secondary text-secondary-foreground inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium">
                                    {tm.roleLabel}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </fieldset>
            </div>
            <SheetFooter
              addSlideoverStyles={presentation === "slideover"}
              className={cn(presentation === "modal" && "px-6 pb-6")}
            >
              <div className="flex flex-col gap-y-2">
                <div className="flex justify-end space-x-2">
                  <FormCancelButton
                    setOpen={setOpen}
                    isDirty={
                      teamForm.formState.isDirty ||
                      assigneeForm.formState.isDirty
                    }
                  />
                  <FormSubmitButton
                    loading={isTeamFormLoading}
                    disabled={!canCreateTeamConnection}
                  />
                </div>
              </div>
            </SheetFooter>
          </form>
        </Form>
      </TabsContent>
      <TabsContent
        className="mt-0 flex min-h-0 flex-1 flex-col"
        value="assignees"
        forceMount
      >
        <Form {...assigneeForm}>
          <form
            onSubmit={assigneeForm.handleSubmit(onAssigneeSubmit)}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              <fieldset
                className={cn(slideoverFieldsetClassName, "mt-0")}
                disabled={!canCreateAssignee}
              >
                {isAssigneeFormLoading && <FormLoadingOverlay />}
                <SearchableSelectFormField
                  label="Assignee"
                  items={users}
                  {...userDirectory.selectProps}
                  itemsKey="id"
                  formatItemLabel={(item) => formatUsersName(item) ?? ""}
                  register={assigneeForm.register("userId")}
                  placeholder="Select an assignee"
                  isItemsLoading={isUsersLoading}
                  isItemsError={isUsersError}
                />
                <SelectFormField<MatterRole>
                  label="Role"
                  items={matterRoles}
                  itemsKey="id"
                  formatItemLabel={(item) => item?.label}
                  register={assigneeForm.register("roleId")}
                  placeholder="Select a role"
                />

                <div className="col-span-full"></div>
              </fieldset>
            </div>
            <SheetFooter
              addSlideoverStyles={presentation === "slideover"}
              className={cn(presentation === "modal" && "px-6 pb-6")}
            >
              <div className="flex flex-col gap-y-2">
                <div className="flex justify-end space-x-2">
                  <FormCancelButton
                    setOpen={setOpen}
                    isDirty={
                      teamForm.formState.isDirty ||
                      assigneeForm.formState.isDirty
                    }
                  />
                  <FormSubmitButton
                    loading={isAssigneeFormLoading}
                    disabled={!canCreateAssignee}
                  />
                </div>
              </div>
            </SheetFooter>
          </form>
        </Form>
      </TabsContent>
    </Tabs>
  );

  return presentation === "modal" ? (
    <AppModal
      open={open}
      onOpenChange={setOpen}
      title={editing ? "Update assignee" : "Create assignee"}
      description={
        editing ? "Update the assignee below" : "Create a new assignee"
      }
      loading={isLoading}
      isDirty={assigneeForm.formState.isDirty || teamForm.formState.isDirty}
      size="4xl"
      bodyContained
      bodyClassName="min-h-0 p-0"
    >
      {content}
    </AppModal>
  ) : (
    <Slideover
      open={open}
      setOpen={setOpen}
      title={editing ? "Update assignee" : "Create assignee"}
      description={
        editing ? "Update the assignee below" : "Create a new assignee"
      }
      isDirty={assigneeForm.formState.isDirty || teamForm.formState.isDirty}
      isLoading={isLoading}
    >
      {content}
    </Slideover>
  );
};
